import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../../lib/api';

interface Props {
  onOpenPolicy: (type: 'terms' | 'privacy') => void;
}

const navLinks = [
  { label: '产品功能', anchor: 'features' },
  { label: '会员权益', anchor: 'pricing' },
  { label: '激励市场', anchor: 'market' },
  { label: '帮助中心', anchor: '' }
];

export default function LandingNav({ onOpenPolicy }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginTab, setLoginTab] = useState<'wechat' | 'douyin' | 'phone' | 'password'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // 检查维护模式
  useEffect(() => {
    if (loginOpen) {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
      fetch(`${API_URL}/system/status`)
        .then(res => res.json())
        .then(data => {
          if (data.code === 0 && data.data?.maintenance_mode) {
            setMaintenanceMode(true);
          } else {
            setMaintenanceMode(false);
          }
        })
        .catch(() => setMaintenanceMode(false));
    }
  }, [loginOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (phone.length === 11 && countdown === 0) {
      try {
        setLoading(true);
        setError('');
        await api.auth.sendCode({ phone, type: 'login' });
        setCountdown(60);
      } catch (err) {
        setError('发送验证码失败，请重试');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      if (loginTab === 'password') {
        // 密码登录
        response = await api.auth.loginByPhone({ phone, password });
      } else if (loginTab === 'phone') {
        // 验证码登录
        response = await api.auth.loginBySms({ phone, code });
      } else {
        // 微信/抖音扫码 - 演示模式直接用验证码登录
        response = await api.auth.loginBySms({ phone: '13800138000', code: '123456' });
      }

      if (response.code === 0 && response.data) {
        login(response.data);
        setLoginOpen(false);
        navigate('/dashboard');
      } else {
        setError(response.message || '登录失败');
      }
    } catch (err) {
      setError('登录失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  const handleNavClick = (anchor: string) => {
    if (!anchor) return;
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="https://public.readdy.ai/ai/img_res/329b747f-c748-48a9-a4d0-2bc2d9affcc1.png" alt="习惯星球" className="w-9 h-9 object-contain" />
            <span className={`font-bold text-lg tracking-wide ${scrolled ? 'text-[#0A1B4B]' : 'text-white'}`}>习惯星球</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(item => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.anchor)}
                className={`text-sm font-medium transition-colors hover:opacity-100 cursor-pointer whitespace-nowrap bg-transparent border-none ${
                  scrolled ? 'text-gray-600 hover:text-[#0A1B4B]' : 'text-white/80 hover:text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setLoginOpen(true)}
            className={`whitespace-nowrap text-sm font-medium px-5 py-2 rounded-full transition-all duration-200 ${
              scrolled
                ? 'bg-gradient-to-r from-[#0A1B4B] to-[#1850CC] text-white hover:shadow-md hover:scale-105'
                : 'bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30'
            }`}
          >
            登录 / 注册
          </button>
        </div>
      </nav>

      {loginOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setLoginOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-[400px] shadow-2xl relative" onClick={e => e.stopPropagation()}>
            <button className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => setLoginOpen(false)}>
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="flex items-center gap-2 mb-6">
              <img src="https://public.readdy.ai/ai/img_res/329b747f-c748-48a9-a4d0-2bc2d9affcc1.png" alt="logo" className="w-8 h-8" />
              <span className="font-bold text-[#0A1B4B] text-lg">登录习惯星球</span>
            </div>

            {/* 维护模式提示 */}
            {maintenanceMode ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
                  <i className="ri-tools-line text-amber-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">系统维护中</h3>
                <p className="text-sm text-gray-500 mb-6">系统正在进行维护升级，暂时无法登录。<br/>请稍后再试，给您带来不便敬请谅解。</p>
                <button onClick={() => setLoginOpen(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap">
                  关闭
                </button>
              </div>
            ) : (
            <>
            <div className="flex gap-2 mb-6 bg-blue-50 p-1 rounded-xl">
              {(['phone', 'password', 'wechat', 'douyin'] as const).map(tab => (
                <button key={tab} onClick={() => { setLoginTab(tab); setError(''); }} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  loginTab === tab ? 'bg-white text-[#0A1B4B] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                  {tab === 'phone' ? '验证码' : tab === 'password' ? '密码' : tab === 'wechat' ? '微信' : '抖音'}
                </button>
              ))}
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}
            {loginTab === 'phone' ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">手机号码</label>
                  <input
                    type="tel"
                    maxLength={11}
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="请输入手机号"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">验证码</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      maxLength={6}
                      value={code}
                      onChange={e => setCode(e.target.value)}
                      placeholder="请输入验证码（演示：123456）"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                    />
                    <button onClick={handleSendCode} disabled={countdown > 0 || phone.length !== 11 || loading} className={`whitespace-nowrap px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      countdown > 0 || phone.length !== 11 || loading
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#0A1B4B] to-[#1850CC] text-white hover:shadow-md'
                    }`}>
                      {loading ? '发送中...' : countdown > 0 ? `${countdown}s` : '发送验证码'}
                    </button>
                  </div>
                </div>
                <button onClick={handleLogin} disabled={loading} className="w-full bg-gradient-to-r from-[#0A1B4B] to-[#1850CC] text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? '登录中...' : '立即登录'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  登录即表示同意
                  <button onClick={() => { setLoginOpen(false); onOpenPolicy('terms'); }} className="text-blue-600 hover:underline cursor-pointer">用户协议</button>
                  和
                  <button onClick={() => { setLoginOpen(false); onOpenPolicy('privacy'); }} className="text-blue-600 hover:underline cursor-pointer">隐私政策</button>
                </p>
              </div>
            ) : loginTab === 'password' ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">手机号码</label>
                  <input
                    type="tel"
                    maxLength={11}
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="请输入手机号"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">密码</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="请输入密码（演示：123456）"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                  />
                </div>
                <button onClick={handleLogin} disabled={loading} className="w-full bg-gradient-to-r from-[#0A1B4B] to-[#1850CC] text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? '登录中...' : '立即登录'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  登录即表示同意
                  <button onClick={() => { setLoginOpen(false); onOpenPolicy('terms'); }} className="text-blue-600 hover:underline cursor-pointer">用户协议</button>
                  和
                  <button onClick={() => { setLoginOpen(false); onOpenPolicy('privacy'); }} className="text-blue-600 hover:underline cursor-pointer">隐私政策</button>
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center py-4">
                <div className="w-40 h-40 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center mb-4 border border-orange-100">
                  <div className="w-32 h-32 bg-white rounded-xl flex flex-col items-center justify-center gap-2">
                    <i className={`${loginTab === 'wechat' ? 'ri-wechat-fill text-green-500' : 'ri-tiktok-fill text-black'} text-4xl`}></i>
                    <span className="text-xs text-gray-400">{loginTab === 'wechat' ? '微信扫一扫' : '抖音扫一扫'}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500">打开{loginTab === 'wechat' ? '微信' : '抖音'}扫描上方二维码</p>
                <button onClick={handleLogin} className="mt-4 text-xs text-blue-600 hover:underline cursor-pointer">演示：直接进入系统 →</button>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
